import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { ArrowLeftOutlined, UploadOutlined, EyeOutlined, CloseOutlined } from '@ant-design/icons';
import { Form, Input, Select, Button, Table, message, Modal, Spin } from 'antd';
import styled from 'styled-components';
import { ButtonWrapper } from 'components';
import { Box, Flex, Text } from 'atoms';
import { createNode } from 'store/nodeSliceV2';
import { getProjectListV2 } from 'store/projectSliceV2';
import { searchPincodeV2 } from 'store/clientSliceV2';
import { getSignedUrl } from 'store/commonSlice';
import { bulkUpload } from 'store/bulkUploadSlice';
import BulkUploadModal from 'componentsv2/modals/BulkUploadModal';
import AddressPickerV2 from 'components/common/AddressPickerV2/AddressPickerV2';
import loadGoogleMapsV2 from 'utils/loadGoogleMapsV2';
import { truncateAddressToCity } from 'utils/addressHelpers';

const { Option } = Select;

const PageContainer = styled(Box)`
	max-width: 1600px;
	margin: 0 auto;
`;

const FormContainer = styled(Box)`
	background: #fff;
	border: 1px solid #e8e8e8;
	border-radius: 8px;
	padding: 32px 40px;
	margin-bottom: 24px;
	max-width: 1600px;
	margin: 0 auto;
`;

const FormRow = styled(Flex)`
	margin-bottom: 24px;
	gap: 16px;

	&:last-of-type {
		margin-bottom: 0;
	}
`;

const FormItem = styled(Box)`
	flex: 1;

	.ant-form-item-label {
		padding-bottom: 8px;

		label {
			font-size: 13px;
			font-weight: 500;
			color: #333;

			&::before {
				display: none !important;
			}
		}

		label.ant-form-item-required:not(.ant-form-item-required-mark-optional)::after {
			display: inline-block;
			margin-left: 4px;
			color: #333;
			font-size: 13px;
			line-height: 1;
			content: '*';
		}
	}

	.ant-form-item-label
		> label.ant-form-item-required:not(.ant-form-item-required-mark-optional)::before {
		display: none !important;
	}

	.ant-input,
	.ant-select-selector {
		border-radius: 4px;
		height: 40px;
		font-size: 13px;
	}

	.ant-input:disabled,
	.ant-input[disabled] {
		background-color: #f5f5f5;
		color: #333;
		cursor: not-allowed;
	}
`;

const SmallFormItem = styled(FormItem)`
	flex: 0.8;
`;

const MediumFormItem = styled(FormItem)`
	flex: 1.2;
`;

const StyledTable = styled(Table)`
	background: #fff;
	border-radius: 8px;
	overflow: hidden;

	.ant-table-thead > tr > th {
		background: #18405d;
		color: #fff;
		font-weight: 600;
		padding: 12px 16px;
	}
	.ant-table-tbody > tr > td {
		border-bottom: 1px solid #f0f0f0;
		padding: 12px 16px;
	}
	.ant-table-tbody > tr:hover > td {
		background: #f5f5f5;
	}
`;

const DisabledInput = styled(Input)`
	background-color: #f5f5f5 !important;
	color: #333 !important;
	cursor: not-allowed;

	&:hover,
	&:focus {
		border-color: #d9d9d9 !important;
		box-shadow: none !important;
	}
`;

const HeaderTitle = styled(Box)`
	.anticon {
		font-size: 20px;
		color: #333;
		cursor: pointer;
		transition: color 0.3s;

		&:hover {
			color: #1890ff;
		}
	}
`;

const SectionTitle = styled(Text)`
	font-size: 20px;
	font-weight: 600;
	color: #333;
	margin-bottom: 16px;
	display: block;
`;

const NodeTypeOptions = [
	{ value: 'Warehouse', label: 'Warehouse' },
	{ value: 'Store', label: 'Store' },
	{ value: 'Other', label: 'Other' },
];

export default function CreateNodePageV2() {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [form] = Form.useForm();
	const [modalForm] = Form.useForm();
	const [nodeDetails, setNodeDetails] = useState([]);
	const [loading, setLoading] = useState(false);
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [selectedNode, setSelectedNode] = useState(null);
	const [projects, setProjects] = useState([]);
	const [projectsLoading, setProjectsLoading] = useState(false);
	const [bulkUploadModalVisible, setBulkUploadModalVisible] = useState(false);
	const [uploadedS3Key, setUploadedS3Key] = useState(null);
	const [uploadedEntity, setUploadedEntity] = useState(null);
	const [bulkUploadLoading, setBulkUploadLoading] = useState(false);

	console.log('Current projects state:', projects); // Debug log

	// Fetch projects on component mount
	useEffect(() => {
		const loadProjects = async () => {
			try {
				setProjectsLoading(true);
				console.log('Starting to fetch projects...');

				// getProjectListV2 returns a thunk function, call it directly
				const thunk = getProjectListV2({
					filter_by: { is_active: true }, // Only fetch active projects
					pagination: { page: 1, per_page: 1000 },
				});
				const result = await thunk();

				console.log('Full API result:', result);
				console.log('Result.rows:', result?.rows);
				console.log('Is rows an array?', Array.isArray(result?.rows));

				if (result?.rows && Array.isArray(result.rows)) {
					// API returns projects with 'id' field, not 'project_id'
					console.log('Setting projects to state. Count:', result.rows.length);
					console.log('First project:', result.rows[0]);
					setProjects(result.rows);
				} else {
					console.warn(
						'No projects found in response. Full result:',
						JSON.stringify(result)
					);
					setProjects([]);
				}
			} catch (error) {
				message.error('Failed to fetch projects');
				console.error('Error fetching projects. Full error:', error);
				console.error('Error message:', error.message);
				console.error('Error stack:', error.stack);
				setProjects([]);
			} finally {
				setProjectsLoading(false);
				console.log('Projects loading complete');
			}
		};

		loadProjects();
	}, []);

	// Handle pincode lookup - Auto-fetch zone, city, state when user types 6-digit pincode
	const handlePincodeLookup = async (pincode) => {
		if (pincode?.length === 6) {
			try {
				const result = await searchPincodeV2({ pincode })();
				if (result) {
					form.setFieldsValue({
						zone: result.zone || '',
						city: result.city || '',
						city_code: result.city_code || '',
						state: result.state || '',
					});
				} else {
					// Clear fields if pincode not found
					form.setFieldsValue({
						zone: '',
						city: '',
						city_code: '',
						state: '',
					});
				}
			} catch (error) {
				console.error('Pincode lookup failed:', error);
				// Clear fields on error
				form.setFieldsValue({
					zone: '',
					city: '',
					city_code: '',
					state: '',
				});
			}
		} else if (pincode?.length < 6) {
			// Clear fields if user is still typing or deleted characters
			form.setFieldsValue({
				zone: '',
				city: '',
				city_code: '',
				state: '',
			});
		}
	};

	// Handle pincode lookup for modal form
	const handleModalPincodeLookup = async (pincode) => {
		if (pincode?.length === 6) {
			try {
				const result = await searchPincodeV2({ pincode })();
				if (result) {
					modalForm.setFieldsValue({
						zone: result.zone || '',
						city: result.city || '',
						city_code: result.city_code || '',
						state: result.state || '',
					});
				} else {
					// Clear fields if pincode not found
					modalForm.setFieldsValue({
						zone: '',
						city: '',
						city_code: '',
						state: '',
					});
				}
			} catch (error) {
				console.error('Pincode lookup failed:', error);
				// Clear fields on error
				modalForm.setFieldsValue({
					zone: '',
					city: '',
					city_code: '',
					state: '',
				});
			}
		} else if (pincode?.length < 6) {
			// Clear fields if user is still typing or deleted characters
			modalForm.setFieldsValue({
				zone: '',
				city: '',
				city_code: '',
				state: '',
			});
		}
	};

	// Load Google Maps on component mount
	useEffect(() => {
		loadGoogleMapsV2().catch((err) => {
			console.error('Failed to load Google Maps:', err);
			// Only show error if API key is missing, otherwise it might be a network issue
			if (err.message && err.message.includes('REACT_APP_GOOGLE_MAPS_KEY')) {
				message.error(
					'Google Maps API key is missing. Please configure REACT_APP_GOOGLE_MAPS_KEY in your environment variables.'
				);
			} else {
				message.warning(
					'Google Maps is taking longer to load. Address autocomplete may not work until it loads.'
				);
			}
		});
	}, []);

	// Clear validation errors and reset form if there's already at least one node in the table
	useEffect(() => {
		if (nodeDetails.length > 0) {
			// Clear all validation errors by resetting the fields without values
			const allFields = [
				'project_id',
				'node_name',
				'node_type',
				'node_address',
				'latitude',
				'longitude',
				'pincode',
				'zone',
				'city',
				'state',
				'poc_name',
				'poc_number',
			];

			// Clear validation status for all fields
			form.setFields(
				allFields.map((name) => ({
					name,
					errors: [],
					validating: false,
					touched: false,
				}))
			);
		}
	}, [nodeDetails.length, form]);

	// Helper function to validate required fields when adding node
	const validateNodeFields = (values) => {
		// Check if form is empty when there are already nodes
		if (
			nodeDetails.length > 0 &&
			!values.project_id &&
			!values.node_name &&
			!values.node_address
		) {
			return { valid: false, shouldReturn: true };
		}

		// Check required fields
		if (!values.project_id || !values.node_type || !values.node_name || !values.node_address) {
			message.warning('Please fill in all required fields to add a node');
			return { valid: false, shouldReturn: true };
		}

		if (!values.latitude || !values.longitude || !values.pincode) {
			message.warning('Please fill in latitude, longitude, and pincode');
			return { valid: false, shouldReturn: true };
		}

		return { valid: true, shouldReturn: false };
	};

	const handleAddNode = async () => {
		try {
			// Only validate if this is the first node (no nodes in table yet)
			// If there are already nodes, skip validation to allow optional additional nodes
			if (nodeDetails.length === 0) {
				// First validate only required fields
				await form.validateFields([
					'project_id',
					'node_type',
					'node_name',
					'node_address',
					'latitude',
					'longitude',
					'pincode',
				]);
			}

			// Then get all form values including optional fields
			const values = form.getFieldsValue();

			// Validate fields using helper function
			const { shouldReturn } = validateNodeFields(values);
			if (shouldReturn) return;

			// Find the selected project to get its name (API returns 'id' field)
			const selectedProject = projects.find((p) => p.id === values.project_id);

			// Check for duplicate node (same project, node name, and address)
			const isDuplicate = nodeDetails.some(
				(node) =>
					node.project_id === values.project_id &&
					node.node_name.trim().toLowerCase() === values.node_name.trim().toLowerCase() &&
					node.node_address.trim().toLowerCase() ===
						values.node_address.trim().toLowerCase()
			);

			if (isDuplicate) {
				message.error(
					'A node with the same name and address already exists in this project. Please use a different name or address.'
				);
				return;
			}

			const newNode = {
				id: nodeDetails.length + 1,
				project_id: values.project_id, // This will be the project's id from API
				project_name: selectedProject?.project_name || '',
				node_type: values.node_type,
				node_name: values.node_name,
				node_address: values.node_address,
				coordinates: `${values.latitude}-${values.longitude}`,
				poc_name: values.poc_name || '-',
				poc_number: values.poc_number || '-',
				pincode: values.pincode,
				zone: values.zone || '',
				city: values.city || '',
				city_code: values.city_code || '',
				state: values.state || '',
			};

			setNodeDetails([...nodeDetails, newNode]);
			message.success('Node added successfully');

			// Reset all form fields after adding node
			form.resetFields();

			// Clear all validation errors and validation states
			const allFields = [
				'project_id',
				'node_name',
				'node_type',
				'node_address',
				'latitude',
				'longitude',
				'poc_name',
				'poc_number',
				'pincode',
				'zone',
				'city',
				'state',
			];
			form.setFields(
				allFields.map((name) => ({
					name,
					errors: [],
					validating: false,
					touched: false,
				}))
			);
		} catch (error) {
			console.error('Validation failed:', error);
		}
	};

	// Handle bulk upload modal open
	const handleBulkUpload = () => {
		setBulkUploadModalVisible(true);
	};

	// Handle bulk upload modal close
	const handleBulkUploadClose = () => {
		setBulkUploadModalVisible(false);
		setUploadedS3Key(null);
		setUploadedEntity(null);
		setBulkUploadLoading(false);
	};

	// Handle file upload to S3 (called when file is selected in the modal)
	const handleBulkUploadFileToS3 = async (file) => {
		setBulkUploadLoading(true);
		message.loading({
			content: 'Uploading file to S3...',
			key: 'bulkUploadNode',
		});

		try {
			// Step 1: Get signed URL for S3 upload
			const signedUrlResponse = await dispatch(
				getSignedUrl({
					url_type: 'upload',
					data: {
						entity: 'node_bulk_upload_v2',
						keys: [file.name],
						content_type: 'text/csv',
					},
				})
			);

			if (!signedUrlResponse || signedUrlResponse.status !== 'success') {
				throw new Error('Failed to get S3 upload URL');
			}

			const uploadData = signedUrlResponse.data?.[0];
			if (!uploadData?.url || !uploadData?.filename) {
				throw new Error('Invalid S3 upload URL response');
			}

			const { url: uploadUrl, filename: s3Key } = uploadData;
			const entity = 'node_bulk_upload_v2'; // Store entity used for signed URL

			// Step 2: Upload file to S3
			const uploadResponse = await fetch(uploadUrl, {
				method: 'PUT',
				body: file,
				headers: {
					'Content-Type': file.type || 'text/csv',
				},
			});

			if (!uploadResponse.ok) {
				throw new Error(`S3 upload failed: ${uploadResponse.statusText}`);
			}

			// Store both S3 key and entity for later submission
			setUploadedS3Key(s3Key);
			setUploadedEntity(entity);

			message.success({
				content: 'File uploaded successfully',
				key: 'bulkUploadNode',
			});
			setBulkUploadLoading(false);
		} catch (error) {
			message.error({
				content: error.message || 'Failed to upload file',
				key: 'bulkUploadNode',
			});
			setBulkUploadLoading(false);
		}
	};

	// Handle bulk upload submit (called when user clicks Submit in modal)
	const handleBulkUploadSubmit = async () => {
		if (!uploadedS3Key) {
			message.warning('Please upload a file first');
			return;
		}

		setBulkUploadLoading(true);
		message.loading({
			content: 'Processing bulk upload...',
			key: 'bulkUploadNode',
		});

		try {
			const result = await dispatch(
				bulkUpload({
					upload_type: 'node',
					s3_key: uploadedS3Key,
					entity: uploadedEntity,
				})
			);

			if (result?.status === 'success') {
				const { success_count, error_count, total_count, download_url } = result.data || {};

				// Show detailed success message
				if (error_count > 0) {
					message.warning({
						content: `Bulk upload completed: ${success_count} succeeded, ${error_count} failed out of ${total_count} total`,
						key: 'bulkUploadNode',
						duration: 5,
					});

					// If there's a download URL with errors, show info
					if (download_url) {
						message.info({
							content: 'Download the CSV file to see error details',
							duration: 5,
						});
					}
				} else {
					message.success({
						content: `Successfully uploaded ${success_count} nodes`,
						key: 'bulkUploadNode',
					});
				}

				// Close modal and refresh page
				handleBulkUploadClose();
				navigate('/nodes'); // Navigate to node list page
			} else {
				throw new Error(result?.error?.message || 'Bulk upload failed');
			}
		} catch (error) {
			message.error({
				content: error.message || 'Failed to process bulk upload',
				key: 'bulkUploadNode',
			});
		} finally {
			setBulkUploadLoading(false);
		}
	};

	// Helper function to prepare node data for API
	const prepareNodeData = (node) => {
		const [latitude, longitude] = node.coordinates.split('-');
		return {
			project_id: node.project_id,
			node_type: node.node_type,
			node_name: node.node_name,
			node_address: {
				line1: node.node_address,
				line2: '',
				city: node.city || '',
				city_code: node.city_code || '',
				state: node.state || '',
				pincode: node.pincode,
				country: 'India',
			},
			latitude: parseFloat(latitude),
			longitude: parseFloat(longitude),
			pincode: node.pincode,
			poc_name: node.poc_name === '-' ? '' : node.poc_name,
			poc_mobile_number: node.poc_number === '-' ? '' : node.poc_number,
			poc_email: '',
		};
	};

	// Helper function to submit a single node
	const submitSingleNode = async (node) => {
		try {
			const nodeData = prepareNodeData(node);
			await dispatch(createNode({ req_data: nodeData }));
			return { success: true, nodeName: node.node_name };
		} catch (error) {
			const errorMsg = error?.response?.data?.error?.message || error.message;
			const errorCode = error?.response?.data?.error?.code;

			if (errorCode === 'DUPLICATE_ADDRESS') {
				message.error(`Node "${node.node_name}": ${errorMsg}`);
			}

			return { success: false, nodeName: node.node_name, error: errorMsg };
		}
	};

	// Helper function to show submission results
	const showSubmissionResults = (results, totalNodes) => {
		const successCount = results.filter((r) => r.success).length;
		const failedCount = totalNodes - successCount;

		if (successCount === totalNodes) {
			message.success(`All ${successCount} node(s) created successfully`);
			navigate('/nodes');
		} else if (successCount > 0) {
			message.warning(`${successCount} node(s) created, ${failedCount} failed`);
		} else {
			message.error('Failed to create any nodes. Please check the errors and try again.');
		}
	};

	const handleSubmit = async () => {
		if (nodeDetails.length === 0) {
			message.warning('Please add at least one node before submitting');
			return;
		}

		setLoading(true);

		try {
			// Submit all nodes in parallel using Promise.allSettled
			const nodePromises = nodeDetails.map((node) => submitSingleNode(node));
			const results = await Promise.allSettled(nodePromises);

			// Extract values from settled promises
			const processedResults = results.map((result) =>
				result.status === 'fulfilled' ? result.value : { success: false }
			);

			showSubmissionResults(processedResults, nodeDetails.length);
		} catch (error) {
			message.error('An unexpected error occurred during submission');
			console.error('Submission error:', error);
		} finally {
			setLoading(false);
		}
	};

	const handleViewNode = (record) => {
		setSelectedNode(record);
		// Split coordinates back to latitude and longitude
		const [latitude, longitude] = record.coordinates.split('-');
		modalForm.setFieldsValue({
			project_id: record.project_id,
			node_type: record.node_type,
			node_name: record.node_name,
			node_address: record.node_address,
			latitude,
			longitude,
			poc_name: record.poc_name === '-' ? '' : record.poc_name,
			poc_number: record.poc_number === '-' ? '' : record.poc_number,
			pincode: record.pincode,
			zone: record.zone || '',
			city: record.city || '',
			city_code: record.city_code || '',
			state: record.state || '',
		});
		setIsModalVisible(true);
	};

	const handleModalClose = () => {
		setIsModalVisible(false);
		setSelectedNode(null);
		modalForm.resetFields();
	};

	const handleRemoveNode = () => {
		setNodeDetails(nodeDetails.filter((node) => node.id !== selectedNode.id));
		message.success('Node removed successfully');
		handleModalClose();
	};

	const handleSaveChanges = async () => {
		try {
			const values = await modalForm.validateFields();

			// Find the selected project to get its name (API returns 'id' field)
			const selectedProject = projects.find((p) => p.id === values.project_id);

			const updatedNode = {
				...selectedNode,
				project_id: values.project_id,
				project_name: selectedProject?.project_name || '',
				node_type: values.node_type,
				node_name: values.node_name,
				node_address: values.node_address,
				coordinates: `${values.latitude}-${values.longitude}`,
				poc_name: values.poc_name || '-',
				poc_number: values.poc_number || '-',
				pincode: values.pincode,
				zone: values.zone || '',
				city: values.city || '',
				city_code: values.city_code || '',
				state: values.state || '',
			};

			setNodeDetails(
				nodeDetails.map((node) => (node.id === selectedNode.id ? updatedNode : node))
			);
			message.success('Node updated successfully');
			handleModalClose();
		} catch (error) {
			console.error('Validation failed:', error);
		}
	};

	const tableColumns = [
		{
			title: 'Project Name',
			dataIndex: 'project_name',
			key: 'project_name',
		},
		{
			title: 'Node Type',
			dataIndex: 'node_type',
			key: 'node_type',
		},
		{
			title: 'Node Name',
			dataIndex: 'node_name',
			key: 'node_name',
		},
		{
			title: 'Node Address',
			dataIndex: 'node_address',
			key: 'node_address',
		},
		{
			title: 'Coordinates',
			dataIndex: 'coordinates',
			key: 'coordinates',
		},
		{
			title: 'PoC Name',
			dataIndex: 'poc_name',
			key: 'poc_name',
		},
		{
			title: 'PoC Number',
			dataIndex: 'poc_number',
			key: 'poc_number',
		},
		{
			title: 'Pincode',
			dataIndex: 'pincode',
			key: 'pincode',
		},
		{
			title: 'Actions',
			key: 'actions',
			width: '80px',
			align: 'center',
			render: (_, record) => (
				<EyeOutlined
					style={{
						color: '#1890ff',
						fontSize: '18px',
						cursor: 'pointer',
					}}
					onClick={() => handleViewNode(record)}
				/>
			),
		},
	];

	return (
		<Box>
			{/* Header Section */}
			<PageContainer>
				<Flex justifyContent="space-between" alignItems="flex-start" mb={4}>
					<HeaderTitle>
						<Flex alignItems="center">
							<ArrowLeftOutlined onClick={() => navigate('/nodes')} />
							<Box ml={3}>
								<Text fontSize="24px" fontWeight="600" color="#333" mb="4px">
									Create Node
								</Text>
								<Text fontSize="14px" color="#666">
									Create Node for Store and Warehouse Locations
								</Text>
							</Box>
						</Flex>
					</HeaderTitle>

					<ButtonWrapper
						onClick={handleBulkUpload}
						icons={<UploadOutlined />}
						style={{ border: '1px solid #091744' }}
					>
						BULK UPLOAD
					</ButtonWrapper>
				</Flex>
			</PageContainer>

			{/* Create Node Form */}
			<FormContainer>
				<Form form={form} layout="vertical">
					<FormRow>
						<FormItem>
							<Form.Item
								label="Project Name"
								name="project_id"
								required={nodeDetails.length === 0}
								rules={
									nodeDetails.length === 0
										? [{ required: true, message: 'Project name is required' }]
										: []
								}
							>
								<Select
									placeholder="Select Project"
									showSearch
									loading={projectsLoading}
									notFoundContent={
										projectsLoading ? (
											<Spin size="small" />
										) : (
											'No projects found'
										)
									}
									filterOption={(input, option) =>
										(option?.children ?? '')
											.toLowerCase()
											.includes(input.toLowerCase())
									}
									onDropdownVisibleChange={(open) => {
										if (open) {
											console.log(
												'Main form dropdown opened, projects count:',
												projects.length
											);
											console.log('Projects data:', projects);
										}
									}}
								>
									{projects.length > 0
										? projects.map((project) => (
												<Option key={project.id} value={project.id}>
													{project.project_name}
												</Option>
										  ))
										: !projectsLoading && (
												<Option disabled>No projects available</Option>
										  )}
								</Select>
							</Form.Item>
						</FormItem>
						<FormItem>
							<Form.Item
								label="Node Type"
								name="node_type"
								required={nodeDetails.length === 0}
								rules={
									nodeDetails.length === 0
										? [{ required: true, message: 'Node type is required' }]
										: []
								}
							>
								<Select placeholder="Select" showSearch>
									{NodeTypeOptions.map((option) => (
										<Option key={option.value} value={option.value}>
											{option.label}
										</Option>
									))}
								</Select>
							</Form.Item>
						</FormItem>
					</FormRow>

					<FormRow>
						<FormItem>
							<Form.Item
								label="Node Name"
								name="node_name"
								required={nodeDetails.length === 0}
								rules={
									nodeDetails.length === 0
										? [{ required: true, message: 'Node name is required' }]
										: []
								}
							>
								<Input placeholder="Enter node name" />
							</Form.Item>
						</FormItem>
						<FormItem>
							<Form.Item
								label="Node Address"
								name="node_address"
								required={nodeDetails.length === 0}
								rules={
									nodeDetails.length === 0
										? [{ required: true, message: 'Node address is required' }]
										: []
								}
							>
								<AddressPickerV2
									value={form.getFieldValue('node_address') || ''}
									latitude={form.getFieldValue('latitude') || ''}
									longitude={form.getFieldValue('longitude') || ''}
									onChange={(data) => {
										// Truncate address to city (safeguard - AddressPickerV2 already truncates, but ensure consistency)
										const truncatedAddress = truncateAddressToCity(
											data.address,
											data.city || ''
										);
										form.setFieldsValue({
											node_address: truncatedAddress,
											latitude: data.latitude,
											longitude: data.longitude,
											// Optionally update state and pincode if they exist in form
											state: data.state || form.getFieldValue('state'),
											pincode: data.pincode || form.getFieldValue('pincode'),
										});
										// Auto-fetch zone and city if pincode is valid
										if (
											data.pincode &&
											data.pincode.length === 6 &&
											/^\d{6}$/.test(data.pincode)
										) {
											handlePincodeLookup(data.pincode);
										}
									}}
									placeholder="Eg : Intelligent Retail,....."
								/>
							</Form.Item>
						</FormItem>
					</FormRow>

					{/* Latitude and Longitude fields - auto-populated by AddressPicker */}
					<FormRow>
						<FormItem>
							<Form.Item
								label="Latitude"
								name="latitude"
								required={nodeDetails.length === 0}
								rules={
									nodeDetails.length === 0
										? [{ required: true, message: 'Latitude is required' }]
										: []
								}
							>
								<Input placeholder="Auto-filled from address" readOnly />
							</Form.Item>
						</FormItem>
						<FormItem>
							<Form.Item
								label="Longitude"
								name="longitude"
								required={nodeDetails.length === 0}
								rules={
									nodeDetails.length === 0
										? [{ required: true, message: 'Longitude is required' }]
										: []
								}
							>
								<Input placeholder="Auto-filled from address" readOnly />
							</Form.Item>
						</FormItem>
					</FormRow>

					<FormRow>
						<MediumFormItem>
							<Form.Item label="PoC Name*" name="poc_name">
								<Input placeholder="Eg : AB 01 AB 1234" />
							</Form.Item>
						</MediumFormItem>
						<MediumFormItem>
							<Form.Item
								label="PoC Number*"
								name="poc_number"
								rules={[
									{
										pattern: /^[0-9]{10}$/,
										message: 'Please enter a valid 10-digit mobile number',
									},
								]}
							>
								<Input placeholder="Eg : 9876543210" maxLength={10} />
							</Form.Item>
						</MediumFormItem>
					</FormRow>

					<FormRow>
						<SmallFormItem>
							<Form.Item
								label="Pincode"
								name="pincode"
								required={nodeDetails.length === 0}
								rules={
									nodeDetails.length === 0
										? [{ required: true, message: 'Pincode is required' }]
										: []
								}
							>
								<Input
									placeholder="Eg : 517408"
									maxLength={6}
									onChange={(e) => handlePincodeLookup(e.target.value)}
								/>
							</Form.Item>
						</SmallFormItem>
						<MediumFormItem>
							<Form.Item label="Zone" name="zone">
								<DisabledInput disabled />
							</Form.Item>
						</MediumFormItem>
						<MediumFormItem>
							<Form.Item label="City" name="city">
								<DisabledInput disabled />
							</Form.Item>
						</MediumFormItem>
						<MediumFormItem>
							<Form.Item label="State" name="state">
								<DisabledInput disabled />
							</Form.Item>
						</MediumFormItem>
						{/* Hidden field to store city_code */}
						<Form.Item name="city_code" hidden>
							<Input />
						</Form.Item>
					</FormRow>

					<Flex justifyContent="center" mt={4}>
						<ButtonWrapper
							onClick={handleAddNode}
							type="primary"
							style={{
								maxWidth: '160px',
								height: '42px',
								fontSize: '14px',
								fontWeight: '500',
								color: '#FFFFFF',
								backgroundColor: '#18405D',
							}}
						>
							Add Node
						</ButtonWrapper>
					</Flex>
				</Form>
			</FormContainer>

			{/* Node Details Table */}
			<Box mt={4} mb={4}>
				<SectionTitle>Node Details</SectionTitle>
				<StyledTable
					rowKey="id"
					columns={tableColumns}
					dataSource={nodeDetails}
					pagination={false}
					locale={{
						emptyText: 'No nodes added yet. Add nodes using the form above.',
					}}
				/>
			</Box>

			{/* Submit Button */}
			<Flex justifyContent="flex-end" mt={3}>
				<ButtonWrapper
					onClick={handleSubmit}
					loading={loading}
					type="primary"
					style={{
						minWidth: '160px',
						height: '42px',
						color: '#FFFFFF',
						backgroundColor: '#18405D',
						fontWeight: '500',
					}}
				>
					SUBMIT
				</ButtonWrapper>
			</Flex>

			{/* Node Information Modal */}
			<Modal
				open={isModalVisible}
				onCancel={handleModalClose}
				footer={null}
				width={700}
				closeIcon={<CloseOutlined />}
				styles={{
					header: {
						borderBottom: '1px solid #e8e8e8',
						marginBottom: '24px',
					},
					body: {
						padding: '24px',
					},
				}}
				title={
					<Text fontSize="20px" fontWeight="600" color="#333">
						Node Information
					</Text>
				}
			>
				<Form form={modalForm} layout="vertical">
					<FormRow>
						<FormItem>
							<Form.Item
								label="Project Name"
								name="project_id"
								required
								rules={[{ required: true, message: 'Project name is required' }]}
							>
								<Select
									placeholder="Select Project"
									showSearch
									loading={projectsLoading}
									notFoundContent={
										projectsLoading ? (
											<Spin size="small" />
										) : (
											'No projects found'
										)
									}
									filterOption={(input, option) =>
										(option?.children ?? '')
											.toLowerCase()
											.includes(input.toLowerCase())
									}
								>
									{projects.map((project) => (
										<Option key={project.id} value={project.id}>
											{project.project_name}
										</Option>
									))}
								</Select>
							</Form.Item>
						</FormItem>
						<FormItem>
							<Form.Item
								label="Node Type"
								name="node_type"
								required
								rules={[{ required: true, message: 'Node type is required' }]}
							>
								<Select placeholder="Select" showSearch>
									{NodeTypeOptions.map((option) => (
										<Option key={option.value} value={option.value}>
											{option.label}
										</Option>
									))}
								</Select>
							</Form.Item>
						</FormItem>
					</FormRow>

					<FormRow>
						<FormItem>
							<Form.Item
								label="Node Name"
								name="node_name"
								required
								rules={[{ required: true, message: 'Node name is required' }]}
							>
								<Input placeholder="Enter node name" />
							</Form.Item>
						</FormItem>
						<FormItem>
							<Form.Item
								label="Node Address"
								name="node_address"
								required
								rules={[{ required: true, message: 'Node address is required' }]}
							>
								<AddressPickerV2
									value={modalForm.getFieldValue('node_address') || ''}
									latitude={modalForm.getFieldValue('latitude') || ''}
									longitude={modalForm.getFieldValue('longitude') || ''}
									onChange={(data) => {
										// Truncate address to city (safeguard - AddressPickerV2 already truncates, but ensure consistency)
										const truncatedAddress = truncateAddressToCity(
											data.address,
											data.city || ''
										);
										modalForm.setFieldsValue({
											node_address: truncatedAddress,
											latitude: data.latitude,
											longitude: data.longitude,
											// Optionally update state and pincode if they exist in form
											state: data.state || modalForm.getFieldValue('state'),
											pincode:
												data.pincode || modalForm.getFieldValue('pincode'),
										});
										// Auto-fetch zone and city if pincode is valid
										if (
											data.pincode &&
											data.pincode.length === 6 &&
											/^\d{6}$/.test(data.pincode)
										) {
											handleModalPincodeLookup(data.pincode);
										}
									}}
									placeholder="Enter node address"
								/>
							</Form.Item>
						</FormItem>
					</FormRow>

					{/* Latitude and Longitude fields - auto-populated by AddressPicker */}
					<FormRow>
						<FormItem>
							<Form.Item
								label="Latitude"
								name="latitude"
								required
								rules={[{ required: true, message: 'Latitude is required' }]}
							>
								<Input placeholder="Auto-filled from address" readOnly />
							</Form.Item>
						</FormItem>
						<FormItem>
							<Form.Item
								label="Longitude"
								name="longitude"
								required
								rules={[{ required: true, message: 'Longitude is required' }]}
							>
								<Input placeholder="Auto-filled from address" readOnly />
							</Form.Item>
						</FormItem>
					</FormRow>

					<FormRow>
						<FormItem>
							<Form.Item label="PoC Name" name="poc_name">
								<Input placeholder="Enter PoC name" />
							</Form.Item>
						</FormItem>
						<FormItem>
							<Form.Item
								label="PoC Number"
								name="poc_number"
								rules={[
									{
										pattern: /^[0-9]{10}$/,
										message: 'Please enter a valid 10-digit mobile number',
									},
								]}
							>
								<Input placeholder="Eg : 9876543210" maxLength={10} />
							</Form.Item>
						</FormItem>
					</FormRow>

					<FormRow>
						<FormItem>
							<Form.Item
								label="Pincode"
								name="pincode"
								required
								rules={[{ required: true, message: 'Pincode is required' }]}
							>
								<Input
									placeholder="Eg : 517408"
									maxLength={6}
									onChange={(e) => handleModalPincodeLookup(e.target.value)}
								/>
							</Form.Item>
						</FormItem>
						<FormItem>
							<Form.Item label="Zone" name="zone">
								<Input disabled />
							</Form.Item>
						</FormItem>
					</FormRow>

					<FormRow>
						<FormItem>
							<Form.Item label="City" name="city">
								<Input disabled />
							</Form.Item>
						</FormItem>
						<FormItem>
							<Form.Item label="State" name="state">
								<Input disabled />
							</Form.Item>
						</FormItem>
						{/* Hidden field to store city_code */}
						<Form.Item name="city_code" hidden>
							<Input />
						</Form.Item>
					</FormRow>

					<Flex justifyContent="space-between" mt={4}>
						<Button
							onClick={handleRemoveNode}
							style={{
								minWidth: '140px',
								height: '42px',
								fontSize: '14px',
								fontWeight: '500',
								color: '#FF4D4F',
								borderColor: '#FF4D4F',
							}}
						>
							Remove Node
						</Button>
						<ButtonWrapper
							onClick={handleSaveChanges}
							type="primary"
							style={{
								minWidth: '160px',
								height: '42px',
								fontSize: '14px',
								fontWeight: '500',
								color: '#FFFFFF',
								backgroundColor: '#18405D',
							}}
						>
							Save Changes
						</ButtonWrapper>
					</Flex>
				</Form>
			</Modal>

			{/* Bulk Upload Modal */}
			<BulkUploadModal
				visible={bulkUploadModalVisible}
				onClose={handleBulkUploadClose}
				onSubmit={handleBulkUploadSubmit}
				onFileUpload={handleBulkUploadFileToS3}
				title="Bulk Upload Nodes"
				description="Please upload a CSV file with node information. Download the template for the required format."
				templateUrl="/node_bulk_upload_template_v2.csv"
				templateText="Download the CSV template:"
				acceptedFormats=".csv"
				maxSizeMB={10}
				loading={bulkUploadLoading}
			/>
		</Box>
	);
}
